import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WarningOutlineIcon } from '../../components/svgs';
import type { SignatoryCheck, SignatoryStatement } from '../../services/service-signatory';
import { translateSignatoryRole } from '../../util/translateProviderRole';
import styles from './app-signatory-info.module.scss';

export type {
  SignatoryCheck,
  SignatoryPerson,
  SignatoryStatement,
  SignatoryStatementLine,
  SignatoryStatementSigner,
  SignatoryMatch,
  SigningStatus,
} from '../../services/service-signatory';

type RuleRow =
  | { kind: 'wording'; text: string; indent: 0 | 1 }
  | {
      kind: 'signer';
      name: string;
      functions: string[];
      roleText: string;
      text: string;
      indent: 0 | 1;
    };

export type Props = {
  check: SignatoryCheck | null;
  loading: boolean;
  failed: boolean;
};

const COLLAPSED_LINE_COUNT = 8;

const COLLAPSED_SIGNER_COUNT = 3;

const MIN_HIDDEN_LINE_COUNT = 3;

/**
 * Flattens statements into display rows, plus the subset of trailing group-resolved signers
 * (a statement's own `signers`, not a name the wording spells out directly) eligible to collapse.
 */
const toRows = (
  statements: SignatoryStatement[],
): { rows: RuleRow[]; collapsible: Set<RuleRow> } => {
  const rows: RuleRow[] = [];
  const collapsible = new Set<RuleRow>();
  for (const statement of statements) {
    for (const line of statement.lines) {
      rows.push(
        line.signer
          ? { kind: 'signer', ...line.signer, text: line.text, indent: line.indent }
          : { kind: 'wording', text: line.text, indent: line.indent },
      );
    }
    statement.signers.forEach((signer, index) => {
      const row: RuleRow = { kind: 'signer', ...signer, text: signer.name, indent: 1 };
      rows.push(row);
      if (index >= COLLAPSED_SIGNER_COUNT) collapsible.add(row);
    });
  }
  return { rows, collapsible };
};

const SignatoryPanel: FC<Props> = ({ check, loading, failed }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'scriveRequest.signatories.firmateckning' });
  const [tSalesTool] = useTranslation('salesTool');
  const [expanded, setExpanded] = useState(false);

  if (failed) return null;
  if (!check && loading) {
    return <p className={styles.firmateckning}>{t('loading', 'Hämtar firmateckning...')}</p>;
  }
  if (!check || check.status !== 'ok') return null;

  const { source, people, statements, signing } = check;
  const sourceName = source === 'syna' ? 'Syna' : 'Creditsafe';

  const { rows, collapsible } = toRows(statements);
  const hasWording = rows.length > 0;
  // A registered person only counts as a resolved signer once a statement actually names them;
  // no resolved signer at all also covers the rule-holds-for-nobody case, deliberately.
  const hasSigners = rows.some(row => row.kind === 'signer');

  const showToggle =
    rows.length > COLLAPSED_LINE_COUNT && collapsible.size >= MIN_HIDDEN_LINE_COUNT;
  const visibleRows = expanded || !showToggle ? rows : rows.filter(row => !collapsible.has(row));

  const renderStatus = () => {
    if (signing.status === 'none') return null;

    if (signing.status === 'ok') {
      return (
        <p className={`${styles.firmateckningStatus} ${styles.ok}`}>
          {signing.viaCeo
            ? t(
                'status.okViaCeo',
                'Vald avtalstecknare är verkställande direktör och får teckna firman avseende löpande förvaltningsåtgärder.',
              )
            : t('status.ok', 'Valda avtalstecknare motsvarar den registrerade firmateckningen.')}
        </p>
      );
    }

    if (signing.status === 'mismatch') {
      return (
        <div className={`${styles.firmateckningStatus} ${styles.warning}`}>
          <WarningOutlineIcon className={styles.firmateckningWarningIcon} />
          <div>
            <p>
              {hasWording
                ? t(
                    'status.mismatch',
                    'Valda avtalstecknare motsvarar inte den registrerade firmateckningen.',
                  )
                : t(
                    'status.notRegistered',
                    'Valda avtalstecknare finns inte bland de registrerade företrädarna.',
                  )}
            </p>
            {signing.unmatched.length > 0 && (
              <p className={styles.firmateckningMuted}>
                {t('status.unmatched', 'Ingen registrerad företrädare matchar: {{names_list}}.', {
                  names: signing.unmatched,
                })}
              </p>
            )}
            <p className={styles.firmateckningMuted}>
              {t(
                'status.advisory',
                'Kontrollen är informativ och hindrar inte utskick. Firmateckningen visar inte intern delegering, till exempel en inköpschef med rätt att teckna upp till ett visst belopp.',
              )}
            </p>
          </div>
        </div>
      );
    }

    return (
      <p className={`${styles.firmateckningStatus} ${styles.firmateckningMuted}`}>
        {hasWording
          ? t(
              'status.unknownRule',
              'Firmateckningen kunde inte tolkas automatiskt. Läs texten ovan och bedöm själv.',
            )
          : t(
              'status.unknownSource',
              'Firmateckningsregeln finns inte hos {{source}}. Kontrollera själv att valda avtalstecknare får teckna firman.',
              { source: sourceName },
            )}
      </p>
    );
  };

  return (
    <div className={styles.firmateckning}>
      <p className={styles.firmateckningHeading}>
        {hasWording
          ? t('heading.rule', 'Firmateckning enligt {{source}}', { source: sourceName })
          : t('heading.representatives', 'Registrerade företrädare enligt {{source}}', {
              source: sourceName,
            })}
      </p>

      {hasWording && (
        <>
          <div className={styles.firmateckningRule} data-testid="firmateckning-rule">
            {visibleRows.map((row, index) => (
              <span
                key={`${index}-${row.text}`}
                data-indent={row.indent}
                className={
                  row.indent === 0
                    ? undefined
                    : row.kind === 'signer'
                      ? styles.firmateckningSigner
                      : styles.firmateckningEntry
                }
              >
                {row.kind === 'signer' && row.roleText
                  ? `${row.text} — ${translateSignatoryRole(row, tSalesTool)}`
                  : row.text}
              </span>
            ))}
          </div>
          {showToggle && (
            <button
              type="button"
              className={styles.firmateckningToggle}
              onClick={() => setExpanded(prev => !prev)}
            >
              {expanded ? t('showLess', 'Visa mindre') : t('showMore', 'Visa mer')}
            </button>
          )}
        </>
      )}

      {!hasSigners && people.length > 0 && (
        <div className={styles.firmateckningRule} data-testid="firmateckning-people">
          {people.map((person, index) => (
            <span key={`${index}-${person.name}`}>
              {person.roleText
                ? `${person.name} — ${translateSignatoryRole(person, tSalesTool)}`
                : person.name}
            </span>
          ))}
        </div>
      )}

      {renderStatus()}
    </div>
  );
};

export default SignatoryPanel;
